import { Head, Link } from "@inertiajs/react"
import { FileText, Play, Users } from "lucide-react"

import { ChatInterface } from "@/components/chat-interface"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"

// Mock data
const teamData = {
  product: {
    name: "Product Engineering",
    description: "End-to-end development of customer-facing features",
    scenarios: [
      {
        id: "local-setup",
        name: "How to set up the repo locally",
        tags: ["Onboarding", "Local Development"],
        agentic: true,
      },
      {
        id: "feature-toggle",
        name: "How to create and use feature flags",
        tags: ["Feature Flags", "A/B Testing", "Release Management"],
        agentic: false,
      },
      {
        id: "code-review",
        name: "Code review and merge guidelines",
        tags: ["Code Review", "Best Practices"],
        agentic: false,
      },
    ],
    employees: [
      {
        id: "john",
        name: "John Doe",
        role: "Frontend Engineer",
        avatar: "https://randomuser.me/api/portraits/men/28.jpg",
      },
      {
        id: "jane",
        name: "Jane Smith",
        role: "Tech Lead",
        avatar: "https://randomuser.me/api/portraits/women/9.jpg",
      },
    ],
  },
  platform: {
    name: "Platform Team",
    description: "Internal tools, CI/CD, and infrastructure support",
    scenarios: [
      {
        id: "ci-pipeline",
        name: "How to configure CI/CD for a new repo",
        tags: ["CI/CD", "Git", "GitHub"],
        agentic: false,
      },
      {
        id: "log-access",
        name: "How to access and query application logs",
        tags: ["Logging", "Monitoring"],
        agentic: false,
      },
      {
        id: "api-permissions",
        name: "How to request access to internal APIs",
        tags: ["API", "Permissions"],
        agentic: false,
      },
    ],
    employees: [
      {
        id: "bob",
        name: "Bob Johnson",
        role: "Platform Engineer",
        avatar: "https://randomuser.me/api/portraits/men/48.jpg",
      },
      {
        id: "alice",
        name: "Alice Brown",
        role: "DevOps Engineer",
        avatar: "https://randomuser.me/api/portraits/women/27.jpg",
      },
    ],
  },
  design: {
    name: "Design Team",
    description: "User experience, visual design, and design systems",
    scenarios: [
      { id: "design-system", name: "How to contribute to the design system" },
      {
        id: "figma-handoff",
        name: "Figma to dev handoff process",
        tags: ["Design", "Figma"],
        agentic: false,
      },
      {
        id: "user-testing",
        name: "How to run a user research session",
        tags: ["User Research", "Testing"],
        agentic: false,
      },
    ],
    employees: [
      {
        id: "sarah",
        name: "Sarah Wilson",
        role: "UX Designer",
        avatar: "https://randomuser.me/api/portraits/women/7.jpg",
      },
      {
        id: "mike",
        name: "Mike Davis",
        role: "Product Manager",
        avatar: "https://randomuser.me/api/portraits/lego/1.jpg",
      },
    ],
  },
  mobile: {
    name: "Mobile Team",
    description: "Development and maintenance of iOS and Android applications",
    scenarios: [
      {
        id: "mobile-onboarding",
        name: "How to run the mobile app locally",
        tags: ["Mobile", "iOS", "Android"],
        agentic: false,
      },
      {
        id: "firebase-integration",
        name: "How to integrate Firebase into a feature",
        tags: ["Firebase", "Integration"],
        agentic: false,
      },
      {
        id: "app-release",
        name: "How to prepare a mobile release",
        tags: ["Release", "Mobile"],
        agentic: false,
      },
    ],
    employees: [
      {
        id: "emma",
        name: "Emma Davis",
        role: "iOS Developer",
        avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      },
      {
        id: "li",
        name: "Li Wei",
        role: "Android Developer",
        avatar: "https://randomuser.me/api/portraits/men/26.jpg",
      },
    ],
  },
  qa: {
    name: "Quality Assurance",
    description:
      "Testing and validation of product functionality and performance",
    scenarios: [
      {
        id: "test-deployment",
        name: "How to deploy to the test environment",
        tags: ["Testing", "Deployment"],
        agentic: false,
      },
      {
        id: "automated-tests",
        name: "How to write and run automated tests",
        tags: ["Testing", "Automation"],
        agentic: false,
      },
      {
        id: "bug-reporting",
        name: "How to file and track bugs",
        tags: ["Testing", "Bug Reporting"],
        agentic: false,
      },
    ],
    employees: [
      {
        id: "sam",
        name: "Sam Green",
        role: "QA Analyst",
        avatar: "https://randomuser.me/api/portraits/men/10.jpg",
      },
      {
        id: "nina",
        name: "Nina Patel",
        role: "Automation Engineer",
        avatar: "https://randomuser.me/api/portraits/women/1.jpg",
      },
    ],
  },
}

const createTags = (tags: string[]) => {
  return tags.map((tag, index) => (
    <Badge key={index} variant="secondary">
      {tag}
    </Badge>
  ))
}

interface TeamShowProps {
  team_id: string
  org_id: string
}

export default function TeamPage({ team_id, org_id }: TeamShowProps) {
  const team = teamData[team_id as keyof typeof teamData]

  if (!team) {
    return <div className="p-6">Team not found</div>
  }

  return (
    <AppLayout>
      <Head title={`Team: ${team.name}`} />

      <div className="p-6">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2">
            <Users className="text-primary size-6" />
            <h1 className="text-3xl font-bold tracking-tight">{team.name}</h1>
          </div>
          <p className="text-muted-foreground">{team.description}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div>
            <h2 className="mb-4 text-xl font-semibold">Team Members</h2>
            <div className="space-y-3">
              {team.employees.map((employee) => (
                <Card
                  key={employee.id}
                  className="cursor-pointer transition-shadow hover:shadow-lg"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={employee.avatar || "/placeholder.svg"}
                        alt={employee.name}
                        width={40}
                        height={40}
                        className="rounded-full object-cover"
                      />

                      <div>
                        <h3 className="font-medium">{employee.name}</h3>
                        <p className="text-muted-foreground text-sm">
                          {employee.role}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-4 text-xl font-semibold">Scenarios</h2>
            <div className="space-y-3">
              {team.scenarios.map((scenario) => (
                <Link
                  key={scenario.id}
                  href={`/orgs/${org_id}/teams/${team_id}/scenarios/${scenario.id}`}
                  className="block"
                >
                  <Card className="cursor-pointer transition-shadow hover:shadow-lg">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <FileText className="text-primary size-4" />
                        <CardTitle className="text-base">
                          {scenario.name}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        {scenario.tags && createTags(scenario.tags)}
                        {scenario.agentic && (
                          <Button size="sm" variant="outline">
                            <Play className="mr-1 size-3" />
                            Run Agent
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-11 flex">
            <ChatInterface context={team.name} />
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
